/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-17 09:50:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import moment from 'moment-timezone';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { store } from 'src/stores/store';
import { processRes } from 'src/helpers/actions-helper';
import { downloadFile } from 'src/helpers';
import apiService from 'src/services/api-service';
import {
  GET_TOP_SEARCH_TERMS_REQUEST,
  GET_TOP_SEARCH_TERMS_SUCCESS,
  GET_TOP_SEARCH_TERMS_FAILURE,
  GET_TOP_SEARCH_TERM_DETAIL_REQUEST,
  GET_TOP_SEARCH_TERM_DETAIL_SUCCESS,
  GET_TOP_SEARCH_TERM_DETAIL_FAILURE,
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST,
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS,
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE,
  GET_SEARCH_TERMS_WITHOUT_RESULT,
  GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS,
  GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE,
  GET_SEARCHES_WITHOUT_RESULT_RATE,
  GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS,
  GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE,
  GET_TOTAL_SEARCH_REVENUE_REQUEST,
  GET_TOTAL_SEARCH_REVENUE_SUCCESS,
  GET_TOTAL_SEARCH_REVENUE_FAILURE,
  GET_SEARCHES_CLICK_RATE,
  GET_SEARCHES_CLICK_RATE_SUCCESS,
  GET_SEARCHES_CLICK_RATE_FAILURE,
  GET_SEARCHES_WITHOUT_CLICK_RATE,
  GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS,
  GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE,
  GET_CONVERSION_RATE,
  GET_CONVERSION_RATE_SUCCESS,
  GET_CONVERSION_RATE_FAILURE,
  GET_SEARCHES_RATE,
  GET_SEARCHES_RATE_SUCCESS,
  GET_SEARCHES_RATE_FAILURE,
  EXPORT_DATA_REQUEST,
  EXPORT_DATA_SUCCESS,
  EXPORT_DATA_FAILURE,
  CHANGE_VALUE,
  SORT_DATA,
  GET_SEARCHES_WITHOUT_CLICK_REQUEST,
  GET_SEARCHES_WITHOUT_CLICK_SUCCESS,
  GET_SEARCHES_WITHOUT_CLICK_FAILURE,
  GET_TOTAL_REVENUE,
  GET_TOTAL_REVENUE_SUCCESS,
  GET_TOTAL_REVENUE_FAILURE,
  GET_APP_IMPACT_CONVERSION_RATE,
  GET_APP_IMPACT_CONVERSION_RATE_SUCCESS,
  GET_APP_IMPACT_CONVERSION_RATE_FAILURE,
  GET_ORDER_COUNT,
  GET_ORDER_COUNT_SUCCESS,
  GET_ORDER_COUNT_FAILURE,
  GET_AVERAGE_ORDER_VALUE,
  GET_AVERAGE_ORDER_VALUE_SUCCESS,
  GET_AVERAGE_ORDER_VALUE_FAILURE,
  GET_REVENUE_PER_VISITOR,
  GET_REVENUE_PER_VISITOR_SUCCESS,
  GET_REVENUE_PER_VISITOR_FAILURE,
  GET_COLLECTION_CONVERSION_RATE_REQUEST,
  GET_COLLECTION_CONVERSION_RATE_SUCCESS,
  GET_COLLECTION_CONVERSION_RATE_FAILURE,
  GET_COLLECTION_TOP_REQUEST,
  GET_COLLECTION_TOP_SUCCESS,
  GET_COLLECTION_TOP_FAILURE,
  GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST,
  GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS,
  GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE,
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST,
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS,
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS,
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE,
  GET_COLLECTIONS_REQUEST,
  GET_COLLECTIONS_SUCCESS,
  GET_COLLECTIONS_FAILURE,
  GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST,
  GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS,
  GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE,
  GET_RECOMMENDATION_CONVERSION_RATE_REQUEST,
  GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS,
  GET_RECOMMENDATION_CONVERSION_RATE_FAILURE,
  GET_RECOMMENDATION_CLICK_RATE_REQUEST,
  GET_RECOMMENDATION_CLICK_RATE_SUCCESS,
  GET_RECOMMENDATION_CLICK_RATE_FAILURE,
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST,
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS,
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE,
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST,
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS,
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE,
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST,
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS,
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE,
  GET_ANALYTICS_SETTINGS_REQUEST,
  GET_ANALYTICS_SETTINGS_SUCCESS,
  GET_ANALYTICS_SETTINGS_FAILURE,
  UPDATE_ANALYTICS_SETTINGS_REQUEST,
  UPDATE_ANALYTICS_SETTINGS_SUCCESS,
  UPDATE_ANALYTICS_SETTINGS_FAILURE,
  GET_TOP_SEARCH_TERMS_WITH_CLICK,
  GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS,
  GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE
} from 'stores/flow-type/analytics-v2.type';
import { getStoreDate } from 'src/helpers/analytics-helpers';
import {
  TOP_ITEM_IN_DETAIL,
  MAP_EXPORT_FILENAME,
  FEATURES,
  COLLECTION_ANALYTIC_METRIC
} from 'src/constants/analytics';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get analytics settings api
 * @return {object} The RSAA object for API middleware
 */
export const getAnalyticsSettings = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.analytics-v2.get-analytics-settings', {}, [
    GET_ANALYTICS_SETTINGS_REQUEST,
    {
      type: GET_ANALYTICS_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ANALYTICS_SETTINGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getAnalyticsSettings'),
          i18n
        )
    }
  ]);
};

/**
 * call to update analytics settings api
 * @return {object} The RSAA object for API middleware
 */
export const updateAnalyticsSettings = (body, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.analytics-v2.update-analytics-settings', { body }, [
    UPDATE_ANALYTICS_SETTINGS_REQUEST,
    {
      type: UPDATE_ANALYTICS_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.Analytics.Actions.updateAnalyticsSettings_Success'),
          i18n
        );
        // Tracking event
        trackFSEvent('Update Analytics settings', {
          feature: 'Analytics'
        });
        return { data };
      }
    },
    {
      type: UPDATE_ANALYTICS_SETTINGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.updateAnalyticsSettings_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to change value in store
 * @return {object} action object
 */
export const changeValue = (name, value) => {
  return { type: CHANGE_VALUE, payload: { name, value } };
};

/**
 * call to sort data in store
 * @return {object} action object
 */
export const sortData = (name, field, direction, fieldInsensitive = '') => {
  return { type: SORT_DATA, payload: { name, field, direction, fieldInsensitive } };
};

/**
 * call to get search term
 * @return {object} The RSAA object for API middleware
 */
export const getListTopSearchTerms = (from, to, compare, queryExecutionId, top, i18n: I18n) => {
  const today = getStoreDate(new Date());

  const queries = {
    fromDate: from ? from.toISOString() : today.toISOString(),
    toDate: to ? to.toISOString() : today.toISOString(),
    top
  };

  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-top-search-terms', { queries }, [
    GET_TOP_SEARCH_TERMS_REQUEST,
    {
      type: GET_TOP_SEARCH_TERMS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_TOP_SEARCH_TERMS_FAILURE,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopSearchTerms'),
          i18n
        );
        return { ...data };
      }
    }
  ]);
};

/**
 * call to get search term detail
 * @return {object} The RSAA object for API middleware
 */
export const getSearchTermDetail = (
  searchTerm,
  from,
  to,
  compare,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, top };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-search-term-detail',
    { queries, params: { searchTerm } },
    [
      GET_TOP_SEARCH_TERM_DETAIL_REQUEST,
      {
        type: GET_TOP_SEARCH_TERM_DETAIL_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_TOP_SEARCH_TERM_DETAIL_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getTopSearchTermDetail'),
            i18n
          );
          return { ...data };
        }
      }
    ]
  );
};

/**
 * call to get search terms without result
 * @return {object} The RSAA object for API middleware
 */
export const getTopSearchTermsWithoutResult = (
  from,
  to,
  compare,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  let queries = {};

  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, top };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-top-search-terms-without-result',
    { queries },
    [
      GET_SEARCH_TERMS_WITHOUT_RESULT,
      {
        type: GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getSearchTermsWithoutResult_Failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

export const getTopSearchTermsWithClick = (
  from,
  to,
  compare,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const queries = {
    fromDate: from ? from.toISOString() : today.toISOString(),
    toDate: to ? to.toISOString() : today.toISOString(),
    compare,
    top
  };

  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-top-search-terms-with-click',
    { queries },
    [
      GET_TOP_SEARCH_TERMS_WITH_CLICK,
      {
        type: GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getSearchTermsWithClick_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get searches without result rate
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSearchesWithoutResult = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-total-searches-without-result',
    { queries },
    [
      GET_SEARCHES_WITHOUT_RESULT_RATE,
      {
        type: GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getSearchesWithoutResultRate_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get searches click rate
 * @return {object} The RSAA object for API middleware
 */
export const getSearchesClickRate = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-searches-click-rate', { queries }, [
    GET_SEARCHES_CLICK_RATE,
    {
      type: GET_SEARCHES_CLICK_RATE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_SEARCHES_CLICK_RATE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getSearchesClickRate_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get searches click rate
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSearchRevenue = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-total-search-revenue', { queries }, [
    GET_TOTAL_SEARCH_REVENUE_REQUEST,
    {
      type: GET_TOTAL_SEARCH_REVENUE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_TOTAL_SEARCH_REVENUE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalSearchRevenue_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get searches without result rate
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSearchesWithoutClick = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-total-searches-without-click',
    { queries },
    [
      GET_SEARCHES_WITHOUT_CLICK_RATE,
      {
        type: GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getSearchesWithoutClickRate_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get total searches
 * @return {object} The RSAA object for API middleware
 */
export const getTopSearchTermsWithoutClick = (from, to, queryExecutionId, i18n: I18n) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, top: TOP_ITEM_IN_DETAIL };

  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-top-search-terms-without-click',
    { queries },
    [
      GET_SEARCHES_WITHOUT_CLICK_REQUEST,
      {
        type: GET_SEARCHES_WITHOUT_CLICK_SUCCESS,
        payload: async (action, state, res) => ({ data: await getJSON(res) })
      },
      {
        type: GET_SEARCHES_WITHOUT_CLICK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getTotalSearches'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get list collection
 * @return {object} The RSAA object for API middleware
 */
export const getCollections = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.analytics-v2.get-collections', {}, [
    GET_COLLECTIONS_REQUEST,
    {
      type: GET_COLLECTIONS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_COLLECTIONS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getCollections_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get conversion rate
 * @return {object} The RSAA object for API middleware
 */
export const getConversionRate = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-conversion-rate', { queries }, [
    GET_CONVERSION_RATE,
    {
      type: GET_CONVERSION_RATE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_CONVERSION_RATE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getConversionRate_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get (collection) collection conversion rate
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionConversionRate = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-collection-conversion-rate',
    { queries },
    [
      GET_COLLECTION_CONVERSION_RATE_REQUEST,
      {
        type: GET_COLLECTION_CONVERSION_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_COLLECTION_CONVERSION_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionConversionRate_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) top collection
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTop = (from, to, compare = false, queryExecutionId, top, i18n: I18n) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, top: top ?? TOP_ITEM_IN_DETAIL };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-collection-top', { queries }, [
    GET_COLLECTION_TOP_REQUEST,
    {
      type: GET_COLLECTION_TOP_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_COLLECTION_TOP_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getCollectionTop_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get (collection) total filter click
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTotalFilterClick = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-collection-total-filter-click',
    { queries },
    [
      GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST,
      {
        type: GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionTotalFilterClick_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total revenue generated by collection
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTotalRevenueGenerated = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-collection-total-revenue-generated',
    { queries },
    [
      GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST,
      {
        type: GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Analytics.Actions.getCollectionTotalRevenueGenerated_Failure'
            ),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total filter option value combination
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTopFilterOptionValueCombination = (
  from,
  to,
  compare = false,
  queryExecutionId,
  top,
  i18n: I18n,
  collectionId = 'all'
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, collectionId, top: top ?? TOP_ITEM_IN_DETAIL };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-collection-total-filter-option-value-combination',
    { queries },
    [
      GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST,
      {
        type: GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Analytics.Actions.getCollectionTopFilterOptionValueCombination_Failure'
            ),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total filter option value
 * @return {object} The RSAA object for API middleware
 */
export const getCollectionTopFilterOptionValue = (
  from,
  to,
  compare = false,
  queryExecutionId,
  top,
  i18n: I18n,
  collectionId = 'all'
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, collectionId, top: top ?? TOP_ITEM_IN_DETAIL };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-collection-total-filter-option-value',
    { queries },
    [
      GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST,
      {
        type: GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionTopFilterOptionValue_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get searches rate
 * @return {object} The RSAA object for API middleware
 */
export const getTotalSearches = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-total-searches', { queries }, [
    GET_SEARCHES_RATE,
    {
      type: GET_SEARCHES_RATE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_SEARCHES_RATE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getSearchesRate_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to export data
 * @return {object} The RSAA object for API middleware
 */
export const exportData = (
  collectionId,
  collectionLabel,
  from,
  to,
  fileFormat,
  feature,
  metric,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();
  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');

  let queries = {
    fromDate,
    toDate,
    collectionId,
    fileFormat,
    metric,
    feature,
    top: TOP_ITEM_IN_DETAIL
  };

  if (!metric) {
    delete queries.metric;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.export-data', { queries }, [
    EXPORT_DATA_REQUEST,
    {
      type: EXPORT_DATA_SUCCESS,
      payload: async (action, state, res) => {
        // Convert response data into blob and download
        const base64String = await res.text();
        const blob = Buffer.from(base64String, 'base64');
        const formatStartDate = moment.tz(fromDate, timezone).format('YYYY-MM-DD');
        const formatEndDate = moment.tz(toDate, timezone).add(-1, 'days').format('YYYY-MM-DD');
        const formatDates = `${formatStartDate}_${formatEndDate}`;
        const collectionName =
          feature === FEATURES.COLLECTION &&
          (metric === COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE ||
            metric === COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE_COMBINATION)
            ? `_${collectionLabel}_`
            : `_`;
        let fileName = '';
        let fileFormat = '';
        switch (feature) {
          case FEATURES.APP_IMPACT:
            fileName = MAP_EXPORT_FILENAME[feature];
            fileFormat = queries.fileFormat && queries.fileFormat[0] === 'csv' ? 'zip' : 'xlsx';
            break;
          case FEATURES.COLLECTION:
            fileName = MAP_EXPORT_FILENAME[metric];
            fileFormat = queries.fileFormat && queries.fileFormat[0] === 'csv' ? 'csv' : 'xlsx';
            break;
          case FEATURES.RECOMMENDATION:
            fileName = MAP_EXPORT_FILENAME[metric];
            fileFormat = queries.fileFormat && queries.fileFormat[0] === 'csv' ? 'csv' : 'xlsx';
            break;
          case FEATURES.SEARCH:
            fileName = MAP_EXPORT_FILENAME[metric];
            fileFormat = queries.fileFormat && queries.fileFormat[0] === 'csv' ? 'csv' : 'xlsx';
            break;
          default:
            break;
        }

        fileName += `${collectionName}${formatDates}.${fileFormat}`;
        showSABToast(i18n.translate('AdminHTML.Analytics.ExportModal.exportedFiles'));
        downloadFile(blob, fileName);

        // Tracking event
        mixpanelV3Helper.trackEventData('Export Analytics', {
          feature: 'Analytics',
          analyticsType: feature,
          subType: metric
        });

        trackFSEvent('Export Analytics', {
          feature: 'Analytics',
          analyticsType: feature,
          subType: metric
        });
      }
    },
    {
      type: EXPORT_DATA_FAILURE,
      payload: async (action, state, res) =>
        showSABToast(i18n.translate('AdminHTML.Analytics.Actions.exportData'))
    }
  ]);
};

/**
 * call to get search term detail
 * @return {object} The RSAA object for API middleware
 */
export const getTopProductsOnSearch = (from, to, compare, queryExecutionId, top, i18n: I18n) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, compare, top };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-top-products-on-search', { queries }, [
    {
      type: GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST,
      payload: (action, state, res) => {
        return {};
      }
    },
    {
      type: GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTopSearchProductPerformance'),
          i18n
        );
        return { ...data };
      }
    }
  ]);
};

// App impact

/**
 * call to get total revenue
 * @return {object} The RSAA object for API middleware
 */
export const getTotalRevenue = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-total-revenue', { queries }, [
    GET_TOTAL_REVENUE,
    {
      type: GET_TOTAL_REVENUE_SUCCESS,
      payload: async (action, state, res) => ({ data: await getJSON(res) })
    },
    {
      type: GET_TOTAL_REVENUE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getTotalRevenue_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get conversion rate
 * @return {object} The RSAA object for API middleware
 */
export const getAppImpactConversionRate = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-app-impact-conversion-rate',
    { queries },
    [
      GET_APP_IMPACT_CONVERSION_RATE,
      {
        type: GET_APP_IMPACT_CONVERSION_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_APP_IMPACT_CONVERSION_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getConversionRate_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get order count
 * @return {object} The RSAA object for API middleware
 */
export const getOrderCount = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-order-count', { queries }, [
    GET_ORDER_COUNT,
    {
      type: GET_ORDER_COUNT_SUCCESS,
      payload: async (action, state, res) => ({ data: await getJSON(res) })
    },
    {
      type: GET_ORDER_COUNT_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getOrderCount_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get average order value
 * @return {object} The RSAA object for API middleware
 */
export const getAverageOrderValue = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-average-order-value', { queries }, [
    GET_AVERAGE_ORDER_VALUE,
    {
      type: GET_AVERAGE_ORDER_VALUE_SUCCESS,
      payload: async (action, state, res) => ({ data: await getJSON(res) })
    },
    {
      type: GET_AVERAGE_ORDER_VALUE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getAverageOrderValue_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get revenue per visitor
 * @return {object} The RSAA object for API middleware
 */
export const getRevenuePerVisitor = (
  from,
  to,
  compare,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  const queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest('admin-v3.analytics-v2.get-revenue-per-visitor', { queries }, [
    GET_REVENUE_PER_VISITOR,
    {
      type: GET_REVENUE_PER_VISITOR_SUCCESS,
      payload: async (action, state, res) => ({ data: await getJSON(res) })
    },
    {
      type: GET_REVENUE_PER_VISITOR_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Analytics.Actions.getRevenuePerVisitor_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to get (collection) collection conversion rate
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationTotalRevenueGenerated = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-total-revenue',
    { queries },
    [
      GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST,
      {
        type: GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionConversionRate_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) top collection
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationConversionRate = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-conversion-rate',
    { queries },
    [
      GET_RECOMMENDATION_CONVERSION_RATE_REQUEST,
      {
        type: GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_CONVERSION_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionTop_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total filter click
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationClickRate = (
  from,
  to,
  compare = false,
  queryExecutionId,
  comparedExecutionId,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }
  if (comparedExecutionId) {
    queries.comparedExecutionId = comparedExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-click-rate',
    { queries },
    [
      GET_RECOMMENDATION_CLICK_RATE_REQUEST,
      {
        type: GET_RECOMMENDATION_CLICK_RATE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_CLICK_RATE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionTotalFilterClick_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total revenue generated by collection
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationTopPage = (
  from,
  to,
  compare = false,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, top, compare };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-top-page-performance',
    { queries },
    [
      GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST,
      {
        type: GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Analytics.Actions.getCollectionTotalRevenueGenerated_Failure'
            ),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total filter option value combination
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationTopWidget = (
  from,
  to,
  compare,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, top };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-top-widget-performance',
    { queries },
    [
      GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST,
      {
        type: GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.Analytics.Actions.getCollectionTopFilterOptionValueCombination_Failure'
            ),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get (collection) total filter option value
 * @return {object} The RSAA object for API middleware
 */
export const getRecommendationTopProduct = (
  from,
  to,
  compare = false,
  queryExecutionId,
  top,
  i18n: I18n
) => {
  let queries = {};
  const today = getStoreDate(new Date());
  const fromDate = from ? from.toISOString() : today.toISOString();
  const toDate = to ? to.toISOString() : today.toISOString();

  queries = { fromDate, toDate, compare, top };
  if (queryExecutionId) {
    queries.queryExecutionId = queryExecutionId;
  }

  return apiService.sendRequest(
    'admin-v3.analytics-v2.get-recommendations-top-product-performance',
    { queries },
    [
      GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST,
      {
        type: GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Analytics.Actions.getCollectionTopFilterOptionValue_Failure'),
            i18n
          )
      }
    ]
  );
};
